/**
 * Wave E.6 — durable persist for shop + directory search/sort (+ orders list filters).
 * Keys (exact):
 *   customer:shop:searchQuery:{tenant}
 *   customer:directory:shops:{tenant} → { q, sort, orgCode }
 *   customer:directory:orgs → { q, sort }
 *   customer:directoryFavoritesPersist:v1 → { q, sort, orgCode }
 *   customer:directoryOrdersPersist:v1 → { q, sort, orgCode }
 * Not used for payments / help.
 *
 * Orders: client session persist only (this module). Remote directory-ui prefs
 * (`DirectoryUiPreferenceDTO`) have no `orders` slot — do not wire
 * `useDirectoryUiPreferenceSync` for orders; parent hydrates/writes via
 * {@link readDirectoryOrdersPersist} / {@link writeDirectoryOrdersPersist}.
 *
 * Storage: localStorage (survives tab close / browser restart). Reads also
 * migrate legacy sessionStorage values once into localStorage.
 */

import type { DirectorySortId } from '../ui/directoryListFilter.js';

const SHOP_SEARCH_PREFIX = 'customer:shop:searchQuery:';
/** Prefix for per-tenant shops directory persist keys (`{prefix}{tenant}`). */
export const DIRECTORY_SHOPS_PREFIX = 'customer:directory:shops:';
export const DIRECTORY_ORGS_STORAGE_KEY = 'customer:directory:orgs';
export const DIRECTORY_FAVORITES_STORAGE_KEY = 'customer:directoryFavoritesPersist:v1';
/** Account orders list filters — q / sort / orgCode (G9 session persist). */
export const DIRECTORY_ORDERS_STORAGE_KEY = 'customer:directoryOrdersPersist:v1';

/** Platform `/shops` has no URL tenant — stable segment for the shops key. */
export const PLATFORM_DIRECTORY_SHOPS_TENANT = '__platform__';

/** Shops directory allow-list — includes favoriteDesc (server-ranked Oblíbené). */
const DIRECTORY_SHOPS_SORT_IDS: readonly DirectorySortId[] = [
  'nameAsc',
  'nameDesc',
  'orgAsc',
  'distanceAsc',
  'favoriteDesc',
];

/** Orders list allow-list — date/amount/org/name (no distanceAsc/favoriteDesc). */
const DIRECTORY_ORDERS_SORT_IDS: readonly DirectorySortId[] = [
  'dateDesc',
  'dateAsc',
  'amountDesc',
  'amountAsc',
  'orgAsc',
  'nameAsc',
  'nameDesc',
];

/** Matches `ORDERS_DIRECTORY_DEFAULT_SORT` in ordersDirectoryFilter.ts. */
const DIRECTORY_ORDERS_DEFAULT_SORT: DirectorySortId = 'dateDesc';

/** Orgs directory allow-list — nameAsc/nameDesc only (never favoriteDesc). */
type DirectoryOrgsSortId = 'nameAsc' | 'nameDesc';

export interface DirectoryShopsPersistState {
  readonly q: string;
  readonly sort: DirectorySortId;
  readonly orgCode: string | null;
}

/** Favorites directory — same shape as shops (q / sort / orgCode). */
export interface DirectoryFavoritesPersistState {
  readonly q: string;
  readonly sort: DirectorySortId;
  readonly orgCode: string | null;
}

/** Account orders list — same shape as favorites (q / sort / orgCode). */
export interface DirectoryOrdersPersistState {
  readonly q: string;
  readonly sort: DirectorySortId;
  readonly orgCode: string | null;
}

export interface DirectoryOrgsPersistState {
  readonly q: string;
  readonly sort: DirectoryOrgsSortId;
}

function isDirectoryShopsSortId(value: unknown): value is DirectorySortId {
  return (
    typeof value === 'string' &&
    (DIRECTORY_SHOPS_SORT_IDS as readonly string[]).includes(value)
  );
}

function isDirectoryOrdersSortId(value: unknown): value is DirectorySortId {
  return (
    typeof value === 'string' &&
    (DIRECTORY_ORDERS_SORT_IDS as readonly string[]).includes(value)
  );
}

function resolveDirectoryOrgsSort(value: unknown): DirectoryOrgsSortId {
  return value === 'nameDesc' ? 'nameDesc' : 'nameAsc';
}

/**
 * Restore shops sort from storage. `distanceAsc` requires a live geolocation
 * fix — without navigator.geolocation, fall back to nameAsc.
 */
export function resolveRestoredDirectoryShopsSort(sort: DirectorySortId): DirectorySortId {
  if (sort !== 'distanceAsc') {
    return sort;
  }
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.geolocation?.getCurrentPosition !== 'function'
  ) {
    return 'nameAsc';
  }
  return 'distanceAsc';
}

/**
 * Persistable sort for shops directory. Do not write `distanceAsc` without a
 * live user position — unless geolocation is still in flight (`geoPending`).
 */
export function resolvePersistableDirectoryShopsSort(
  sort: DirectorySortId,
  hasUserPosition: boolean,
  geoPending = false,
): DirectorySortId {
  if (sort === 'distanceAsc' && !hasUserPosition && !geoPending) {
    return 'nameAsc';
  }
  return sort;
}

function safeLocalStorage(): Storage | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage;
}

function safeSessionStorage(): Storage | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage;
}

/** Read key from localStorage; if missing, adopt legacy sessionStorage once. */
function readDurableItem(key: string): string | null {
  const local = safeLocalStorage();
  if (local !== null) {
    try {
      const fromLocal = local.getItem(key);
      if (fromLocal !== null) {
        return fromLocal;
      }
    } catch {
      // fall through to session migrate
    }
  }
  const session = safeSessionStorage();
  if (session === null) {
    return null;
  }
  try {
    const fromSession = session.getItem(key);
    if (fromSession === null) {
      return null;
    }
    if (local !== null) {
      try {
        local.setItem(key, fromSession);
        session.removeItem(key);
      } catch {
        // Keep session value readable even if migrate fails.
      }
    }
    return fromSession;
  } catch {
    return null;
  }
}

function writeDurableItem(key: string, value: string): void {
  const local = safeLocalStorage();
  if (local === null) {
    return;
  }
  try {
    local.setItem(key, value);
  } catch {
    // Best-effort — private mode / quota must not block search UX.
  }
  const session = safeSessionStorage();
  if (session !== null) {
    try {
      session.removeItem(key);
    } catch {
      // ignore
    }
  }
}

function removeDurableItem(key: string): void {
  const local = safeLocalStorage();
  if (local !== null) {
    try {
      local.removeItem(key);
    } catch {
      // ignore
    }
  }
  const session = safeSessionStorage();
  if (session !== null) {
    try {
      session.removeItem(key);
    } catch {
      // ignore
    }
  }
}

export function shopSearchQueryStorageKey(tenantCode: string): string {
  return `${SHOP_SEARCH_PREFIX}${tenantCode.trim()}`;
}

export function directoryShopsStorageKey(tenantCode: string): string {
  return `${DIRECTORY_SHOPS_PREFIX}${tenantCode.trim()}`;
}

/**
 * Enumerate tenant segments for all durable shops persist keys in localStorage
 * (+ sessionStorage for migrate parity). Skips empty segments; dedupes across stores.
 */
export function listDirectoryShopsPersistTenantKeys(): string[] {
  const tenants = new Set<string>();
  const collectFrom = (storage: Storage | null): void => {
    if (storage === null) {
      return;
    }
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key === null || !key.startsWith(DIRECTORY_SHOPS_PREFIX)) {
          continue;
        }
        const tenant = key.slice(DIRECTORY_SHOPS_PREFIX.length).trim();
        if (tenant.length > 0) {
          tenants.add(tenant);
        }
      }
    } catch {
      // Best-effort enumeration — private mode / blocked storage must not throw.
    }
  };
  collectFrom(safeLocalStorage());
  collectFrom(safeSessionStorage());
  return [...tenants];
}

export function resolveDirectoryShopsPersistTenant(
  filterTenantCode: string | null | undefined,
): string {
  const trimmed =
    typeof filterTenantCode === 'string' ? filterTenantCode.trim() : '';
  return trimmed.length > 0 ? trimmed : PLATFORM_DIRECTORY_SHOPS_TENANT;
}

/**
 * Sort to apply on hydrate when the durable key may have been deleted (G1).
 * Defaults to `nameAsc` when no persist row exists.
 */
export function resolveDirectoryShopsSortForHydrate(
  tenantCode: string,
): DirectorySortId {
  return readDirectoryShopsPersist(tenantCode)?.sort ?? 'nameAsc';
}

/**
 * Orgs directory hydrate sort — defaults to `nameAsc` when key was deleted.
 */
export function resolveDirectoryOrgsSortForHydrate(): 'nameAsc' | 'nameDesc' {
  return readDirectoryOrgsPersist()?.sort ?? 'nameAsc';
}

/**
 * Favorites directory hydrate sort — defaults to `nameAsc` when key was deleted.
 */
export function resolveDirectoryFavoritesSortForHydrate(): DirectorySortId {
  return readDirectoryFavoritesPersist()?.sort ?? 'nameAsc';
}

/**
 * Orders list hydrate sort — defaults to `dateDesc` when key was deleted.
 */
export function resolveDirectoryOrdersSortForHydrate(): DirectorySortId {
  return readDirectoryOrdersPersist()?.sort ?? DIRECTORY_ORDERS_DEFAULT_SORT;
}

export function readShopSearchQuery(tenantCode: string): string {
  const tenant = tenantCode.trim();
  if (tenant.length === 0) {
    return '';
  }
  const raw = readDurableItem(shopSearchQueryStorageKey(tenant));
  return typeof raw === 'string' ? raw : '';
}

export function writeShopSearchQuery(tenantCode: string, query: string): void {
  const tenant = tenantCode.trim();
  if (tenant.length === 0) {
    return;
  }
  const key = shopSearchQueryStorageKey(tenant);
  if (query.length === 0) {
    removeDurableItem(key);
    return;
  }
  writeDurableItem(key, query);
}

export function readDirectoryShopsPersist(
  tenantCode: string,
): DirectoryShopsPersistState | null {
  const tenant = tenantCode.trim();
  if (tenant.length === 0) {
    return null;
  }
  try {
    const raw = readDurableItem(directoryShopsStorageKey(tenant));
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const q = typeof record.q === 'string' ? record.q : '';
    const rawSort = isDirectoryShopsSortId(record.sort) ? record.sort : 'nameAsc';
    const sort = resolveRestoredDirectoryShopsSort(rawSort);
    const orgCode =
      typeof record.orgCode === 'string' && record.orgCode.trim().length > 0
        ? record.orgCode.trim()
        : null;
    return { q, sort, orgCode };
  } catch {
    return null;
  }
}

export function writeDirectoryShopsPersist(
  tenantCode: string,
  state: DirectoryShopsPersistState,
  options?: {
    readonly hasUserPosition?: boolean;
    /** Keep distanceAsc while getCurrentPosition is in flight (restore path). */
    readonly geoPending?: boolean;
  },
): void {
  const tenant = tenantCode.trim();
  if (tenant.length === 0) {
    return;
  }
  const sort = resolvePersistableDirectoryShopsSort(
    state.sort,
    options?.hasUserPosition === true,
    options?.geoPending === true,
  );
  const key = directoryShopsStorageKey(tenant);
  const isDefault =
    state.q.length === 0 && sort === 'nameAsc' && state.orgCode === null;
  if (isDefault) {
    removeDurableItem(key);
    return;
  }
  writeDurableItem(
    key,
    JSON.stringify({
      q: state.q,
      sort,
      orgCode: state.orgCode,
    } satisfies DirectoryShopsPersistState),
  );
}

export function readDirectoryOrgsPersist(): DirectoryOrgsPersistState | null {
  try {
    const raw = readDurableItem(DIRECTORY_ORGS_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const q = typeof record.q === 'string' ? record.q : '';
    const sort = resolveDirectoryOrgsSort(record.sort);
    return { q, sort };
  } catch {
    return null;
  }
}

export function writeDirectoryOrgsPersist(state: {
  readonly q: string;
  /** Coerced to nameAsc/nameDesc — never persists favoriteDesc/orgAsc/distanceAsc. */
  readonly sort: DirectorySortId;
}): void {
  const sort = resolveDirectoryOrgsSort(state.sort);
  const isDefault = state.q.length === 0 && sort === 'nameAsc';
  if (isDefault) {
    removeDurableItem(DIRECTORY_ORGS_STORAGE_KEY);
    return;
  }
  writeDurableItem(
    DIRECTORY_ORGS_STORAGE_KEY,
    JSON.stringify({ q: state.q, sort } satisfies DirectoryOrgsPersistState),
  );
}

export function readDirectoryFavoritesPersist(): DirectoryFavoritesPersistState | null {
  try {
    const raw = readDurableItem(DIRECTORY_FAVORITES_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const q = typeof record.q === 'string' ? record.q : '';
    const rawSort = isDirectoryShopsSortId(record.sort) ? record.sort : 'nameAsc';
    const sort = resolveRestoredDirectoryShopsSort(rawSort);
    const orgCode =
      typeof record.orgCode === 'string' && record.orgCode.trim().length > 0
        ? record.orgCode.trim()
        : null;
    return { q, sort, orgCode };
  } catch {
    return null;
  }
}

export function writeDirectoryFavoritesPersist(
  state: DirectoryFavoritesPersistState,
  options?: {
    readonly hasUserPosition?: boolean;
    /** Keep distanceAsc while getCurrentPosition is in flight (restore path). */
    readonly geoPending?: boolean;
  },
): void {
  const sort = resolvePersistableDirectoryShopsSort(
    state.sort,
    options?.hasUserPosition === true,
    options?.geoPending === true,
  );
  const isDefault =
    state.q.length === 0 && sort === 'nameAsc' && state.orgCode === null;
  if (isDefault) {
    removeDurableItem(DIRECTORY_FAVORITES_STORAGE_KEY);
    return;
  }
  writeDurableItem(
    DIRECTORY_FAVORITES_STORAGE_KEY,
    JSON.stringify({
      q: state.q,
      sort,
      orgCode: state.orgCode,
    } satisfies DirectoryFavoritesPersistState),
  );
}

export function readDirectoryOrdersPersist(): DirectoryOrdersPersistState | null {
  try {
    const raw = readDurableItem(DIRECTORY_ORDERS_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    const q = typeof record.q === 'string' ? record.q : '';
    const sort = isDirectoryOrdersSortId(record.sort)
      ? record.sort
      : DIRECTORY_ORDERS_DEFAULT_SORT;
    const orgCode =
      typeof record.orgCode === 'string' && record.orgCode.trim().length > 0
        ? record.orgCode.trim()
        : null;
    return { q, sort, orgCode };
  } catch {
    return null;
  }
}

export function writeDirectoryOrdersPersist(
  state: DirectoryOrdersPersistState,
): void {
  const sort = isDirectoryOrdersSortId(state.sort)
    ? state.sort
    : DIRECTORY_ORDERS_DEFAULT_SORT;
  const isDefault =
    state.q.length === 0 &&
    sort === DIRECTORY_ORDERS_DEFAULT_SORT &&
    state.orgCode === null;
  if (isDefault) {
    removeDurableItem(DIRECTORY_ORDERS_STORAGE_KEY);
    return;
  }
  writeDurableItem(
    DIRECTORY_ORDERS_STORAGE_KEY,
    JSON.stringify({
      q: state.q,
      sort,
      orgCode: state.orgCode,
    } satisfies DirectoryOrdersPersistState),
  );
}
